import { focusVisible, measures } from "@quickpoint.me/shared";
import styled, { css, keyframes } from "styled-components";

/* ── Panel ───────────────────────────────────── */

export const ChatPanelContainer = styled.section`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  font-family: "Avantt-Regular", sans-serif;
  z-index: 1;
  overflow: hidden;
`;

/* ── Level Switcher (Tabs) ──────────────────── */

export const TabList = styled.div`
  display: flex;
  position: relative;
  flex-shrink: 0;
  align-items: flex-end;
  height: 100%;
`;

export const Tab = styled.button<{ $active: boolean; $disabled?: boolean }>`
  /* fill the editor-bar strip so the whole tab is a hit target */
  height: 100%;
  padding: 0 16px;
  border: 1px solid ${(props) => props.theme.navigationFrame};
  border-bottom: ${({ $active }) => ($active ? "none" : "1px solid")};
  border-bottom-color: ${(props) => props.theme.navigationFrame};
  background: ${({ $active }) => ($active ? "#fff" : "#f5f5f5")};
  border-radius: 4px 4px 0 0;
  margin-bottom: -1px;
  font-family: ${(props) => props.theme.font};
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ $active, $disabled }) => {
    if ($disabled) return "#999";
    /* 7:1+ on the inactive #f5f5f5 background (AAA) */
    return $active ? "#1a1a1a" : "#4a4a4a";
  }};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;

  ${focusVisible}

  ${({ $disabled, $active }) =>
    !$disabled &&
    css`
      &:hover {
        background: ${$active ? "#fff" : "#e8e8e8"};
        color: #1a1a1a;
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/* ── Message List ───────────────────────────── */

export const MessageListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scroll-behavior: smooth;

  @media (prefers-reduced-motion: reduce) {
    scroll-behavior: auto;
  }
`;

/* ── Message ────────────────────────────────── */

export const MessageArticle = styled.article<{ $role: "user" | "assistant" | "human" }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $role }) => ($role === "user" ? "flex-end" : "flex-start")};
  max-width: 85%;
  align-self: ${({ $role }) => ($role === "user" ? "flex-end" : "flex-start")};
`;

export const MessageBubble = styled.div<{ $role: "user" | "assistant" | "human" }>`
  padding: 10px 14px;
  border-radius: 12px;
  line-height: 1.5;
  font-size: 14px;
  max-width: 80ch;

  ${({ $role }) =>
    $role === "user"
      ? css`
          background: #1a1a1a;
          color: #ffffff;
          border-bottom-right-radius: 4px;
        `
      : css`
          background: #f0f0f0;
          color: #1a1a1a;
          border-bottom-left-radius: 4px;
        `}

  & p {
    margin: 0 0 8px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  & code {
    font-size: 13px;
    background: rgba(0, 0, 0, 0.08);
    padding: 1px 4px;
    border-radius: 3px;
  }
`;

export const MessageLabel = styled.span`
  font-size: 11px;
  color: #888;
  margin-bottom: 4px;
  font-weight: 600;
`;

/* ── Chat Input ─────────────────────────────── */

export const ChatInputForm = styled.form`
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
  flex-shrink: 0;
  align-items: flex-end;
`;

export const ChatTextarea = styled.textarea`
  flex: 1;
  padding: 10px 12px;
  border: 2px solid #ccc;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  min-height: 48px;
  max-height: 140px;
  overflow-y: auto;

  &:focus-visible {
    outline: none;
    border-color: #4a90d9;
    box-shadow: 0 0 0 1px #4a90d9;
  }
`;

export const SendButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  min-height: 48px;
  min-width: 48px;
  border: none;
  background: none;
  color: #ffffff;
  font-family: ${(props) => props.theme.font};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  background: #1a1a1a;
  border-radius: 6px;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #333;
  }

  ${focusVisible}
`;

/* ── Streaming Indicator ────────────────────── */

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

export const StreamingDots = styled.span`
  display: inline-flex;
  gap: 4px;
  align-items: center;
  padding: 4px 0;

  & span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #666;
    animation: ${pulse} 1.4s ease-in-out infinite;

    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }

  @media (prefers-reduced-motion: reduce) {
    & span {
      animation: none;
      opacity: 0.6;
    }
  }
`;

/* ── Inline Element Editor (Level 2) ────────── */

export const InlineEditorContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 12px 0;
  gap: 12px;
  min-height: 0;
  overflow-y: auto;
`;

export const InlineEditorContext = styled.p`
  font-size: 13px;
  color: #555;
  margin: 0;
  padding-left: ${measures.editor.landscape.gutter};
  font-weight: 600;
`;

export const InlineCodeMirrorWrapper = styled.div`
  /* No border — matches Structure page CodeMirror */
`;

export const InlineEditorActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-right: ${measures.editor.landscape.gutter};
`;

export const InlineEditorButton = styled.button<{ $variant?: "primary" | "secondary" }>`
  /* svh/svw sizing collapsed to ~29x23px on phones — floor at the 44px
     WCAG AAA target size (2.5.5), grow with the viewport on desktop */
  height: max(${measures.topBar.landscape.icon}, 44px);
  min-width: max(6svw, 88px);
  padding: 0 1em;
  border-radius: 6px;
  font-family: ${(props) => props.theme.font};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;

  ${({ $variant = "secondary" }) =>
    $variant === "primary"
      ? css`
          background: #1a1a1a;
          color: #ffffff;
          border: none;
          &:hover { background: #333; }
        `
      : css`
          background: none;
          color: #1a1a1a;
          border: 1.5px solid #1a1a1a;
          &:hover { background: #1a1a1a; color: #ffffff; }
        `}

  ${focusVisible}
`;

/* ── REPL Chat CodeMirror (Level 1) ────────── */

/** One quiet line above the chat: human help is a message away. */
export const HumanHelpHint = styled.p`
  margin: 0;
  padding: 6px 14px;
  font-size: 0.78rem;
  color: #595959;
  background: #f7f5ff;
  border-bottom: 1px solid #e5e1f8;
`;

export const ChatCodeMirrorContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 0;
  overflow: hidden;

  .chat-codemirror {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .cm-editor {
    height: 100%;
  }

  .cm-scroller {
    max-height: 75svh;
    overflow: auto !important;
  }
`;

export const CancelOverlay = styled.div`
  position: absolute;
  bottom: 12px;
  right: 16px;
  z-index: 5;
`;

/* ── Visually Hidden (screen reader only) ───── */

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
